import {
    Box,
    Button,
    Checkbox,
    Divider,
    Group,
    NumberInput,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import type { AdPlatform, Line } from '@marketing-tool/shared';

import type { Campaign } from '../../../data/game-report/types';
import { preventEnterSubmit } from '../../../lib/utils';

// LINE Ads gender options with their API codes
const GENDER_OPTIONS = [
    { label: 'Male', code: '1' },
    { label: 'Female', code: '2' },
];

// LINE Ads OS options
const OS_OPTIONS = [
    { label: 'Android', code: 'ANDROID' },
    { label: 'iOS', code: 'IOS' },
];

// LINE Ads placement options with their API codes
const PLACEMENT_OPTIONS = [
    // Home/Chat variations
    { label: 'LINE Home tab', code: '13' },
    { label: 'LINE Chat list', code: '7' },

    // Core LINE features
    { label: 'LINE VOOM', code: '8' },
    { label: 'LINE OpenChat', code: '19' },
    { label: 'LINE Wallet', code: '9' },
    { label: 'LINE Album', code: '29' },
    { label: 'LINE Note', code: '32' },

    // News/Content
    { label: 'LINE NEWS', code: '4' },
    { label: 'LINE TODAY', code: '10' },

    // Points/Shopping
    { label: 'LINE POINT CLUB', code: '5' },
    { label: 'LINE Flyer', code: '17' },
    { label: 'LINE Coupons', code: '18' },
    { label: 'LINE My Card', code: '20' },
    { label: 'LINE Brand Catalog', code: '14' },
    { label: 'LINE Sticker Shop', code: '2' },

    // Other services
    { label: 'LINE Monary', code: '26' },
    { label: 'LINE official account chats', code: '27' },
    { label: 'LINE ENTERTAINMENT Official Account', code: '30' },
    { label: 'LINE GAME Official Account', code: '33' },
    { label: 'LINE family apps', code: '10000' },
    { label: 'LINE Ads network', code: '20000' },
];

// LINE Ads age targeting codes
// Minimum ages represent the lower bound of each age bracket
const AGE_MIN_OPTIONS = [15, 20, 25, 30, 35, 40, 45, 50, 55, 60];
// Maximum ages represent the upper bound of each age bracket
const AGE_MAX_OPTIONS = [14, 19, 24, 29, 34, 39, 44, 49, 54, 59, 64];

export type CreateAdGroupFormData = {
    platform: AdPlatform;
    campaignId: string;
    name: string;
    // Budget field
    dailyBudgetMicro?: number;
    // Targeting fields
    targetingMode?: 'MANUAL' | 'AUTO';
    country?: 'JP' | 'TH' | 'TW';
    genders?: string[];
    ageMin?: number;
    ageMax?: number;
    userOs?: { os: string; verMin?: string; verMax?: string }[];
    placements?: string[];
    // Bidding fields
    autoBidType?: string;
    bidStrategy?: string;
};

type CreateAdGroupFormProps = {
    platform: AdPlatform;
    campaign: Campaign;
    onSubmit: (data: CreateAdGroupFormData) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
    initialData?: Partial<CreateAdGroupFormData>;
};

export const CreateAdGroupForm = ({
    platform,
    campaign,
    onSubmit,
    onCancel,
    isSubmitting,
    initialData,
}: CreateAdGroupFormProps) => {
    const form = useForm<CreateAdGroupFormData>({
        initialValues: {
            platform,
            campaignId: campaign.id.toString(),
            name: initialData?.name || '',
            dailyBudgetMicro: initialData?.dailyBudgetMicro ?? 1_000_000_000, // Default: 1000 yen (~$10)
            targetingMode: initialData?.targetingMode || 'AUTO',
            country: initialData?.country || 'JP',
            genders: initialData?.genders || [],
            ageMin: initialData?.ageMin,
            ageMax: initialData?.ageMax,
            placements: initialData?.placements || [],
            autoBidType: initialData?.autoBidType || 'CLICK',
            bidStrategy: initialData?.bidStrategy,
        },
        onValuesChange: (values, previous) => {
            // AUTO targeting only supports CLICK auto-bid type
            if (values.targetingMode === 'AUTO' && previous.targetingMode !== 'AUTO') {
                form.setFieldValue('autoBidType', 'CLICK');
            }
        },
    });

    const handleSubmit = form.onSubmit((values) => {
        // Validate age range
        if (values.ageMin !== undefined && values.ageMax !== undefined && values.ageMin >= values.ageMax) {
            notifications.show({
                title: 'Validation error',
                message: 'Minimum age must be less than maximum age',
                color: 'red',
            });
            return;
        }

        onSubmit(values);
    });

    const toggleGender = (code: string) => {
        const current = form.values.genders || [];
        form.setFieldValue('genders', current.includes(code) ? current.filter((g) => g !== code) : [...current, code]);
    };

    const toggleOS = (os: string) => {
        const currentOs = form.values.userOs || [];
        const hasOS = currentOs.some((item) => item.os === os);
        form.setFieldValue('userOs', hasOS ? currentOs.filter((item) => item.os !== os) : [...currentOs, { os }]);
    };

    const togglePlacement = (code: string) => {
        const current = form.values.placements || [];
        form.setFieldValue(
            'placements',
            current.includes(code) ? current.filter((p) => p !== code) : [...current, code],
        );
    };

    // Campaign objective determines available bidding options
    const lineCampaign = campaign as Line.Campaign;
    const campaignObjective = lineCampaign.campaignObjective || 'VISIT_MY_WEBSITE';

    // Build bidding optimization data based on campaign objective and targeting mode
    const biddingOptimizationData = [
        { value: 'MANUAL', label: 'Manual Bidding' },
        { value: 'CLICK', label: 'Optimize for Clicks' },
        ...(campaignObjective === 'WEBSITE_CONVERSION' && form.values.targetingMode !== 'AUTO'
            ? [
                  { value: 'CONVERSION', label: 'Optimize for Conversions' },
                  { value: 'CUSTOM_CONVERSION', label: 'Optimize for Custom Conversions' },
              ]
            : []),
    ];

    return (
        <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <Stack gap="lg">
                <Paper bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))" radius="sm" p="md">
                    <Text fw={600} mb="xs">
                        Campaign
                    </Text>
                    <Text c="dimmed">{campaign.name}</Text>
                </Paper>

                <TextInput
                    label="Ad Group Name"
                    placeholder="Enter ad group name"
                    required
                    disabled={isSubmitting}
                    {...form.getInputProps('name')}
                />

                <NumberInput
                    label="Daily Budget (JPY)"
                    min={0}
                    step={1}
                    value={form.values.dailyBudgetMicro ? form.values.dailyBudgetMicro / 1_000_000 : ''}
                    onChange={(val) => {
                        const value = typeof val === 'number' ? val : parseFloat(String(val));
                        form.setFieldValue('dailyBudgetMicro', isNaN(value) ? undefined : value * 1_000_000);
                    }}
                    placeholder="Enter daily budget"
                    required
                    disabled={isSubmitting}
                    description="Daily budget in Japanese Yen (default: 1,000 JPY ≈ $10)"
                />

                {/* Targeting Section */}
                <Divider />
                <Box>
                    <Title order={4} mb="md">
                        Targeting
                    </Title>

                    <Stack gap="md">
                        <Select
                            label="Targeting Mode"
                            required
                            data={[
                                { value: 'AUTO', label: 'Auto Targeting' },
                                { value: 'MANUAL', label: 'Manual Targeting' },
                            ]}
                            description="Auto targeting lets LINE optimize your audience automatically"
                            disabled={isSubmitting}
                            value={form.values.targetingMode}
                            onChange={(val) => {
                                if (val !== null) form.setFieldValue('targetingMode', val as 'MANUAL' | 'AUTO');
                            }}
                        />

                        {form.values.targetingMode === 'MANUAL' && (
                            <>
                                <Select
                                    label="Country"
                                    required
                                    data={[
                                        { value: 'JP', label: 'Japan' },
                                        { value: 'TH', label: 'Thailand' },
                                        { value: 'TW', label: 'Taiwan' },
                                    ]}
                                    disabled={isSubmitting}
                                    value={form.values.country}
                                    onChange={(val) => {
                                        if (val !== null) form.setFieldValue('country', val as 'JP' | 'TH' | 'TW');
                                    }}
                                />

                                <Box>
                                    <Text fw={500} mb={4}>
                                        Gender
                                    </Text>
                                    <Stack gap="xs">
                                        {GENDER_OPTIONS.map((gender) => (
                                            <Checkbox
                                                key={gender.code}
                                                checked={form.values.genders?.includes(gender.code) || false}
                                                onChange={() => toggleGender(gender.code)}
                                                disabled={isSubmitting}
                                                label={gender.label}
                                            />
                                        ))}
                                    </Stack>
                                    <Text size="xs" c="dimmed" mt={4}>
                                        Leave unchecked to target all genders
                                    </Text>
                                </Box>

                                <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md">
                                    <Select
                                        label="Minimum Age"
                                        value={form.values.ageMin?.toString() || ''}
                                        onChange={(val) =>
                                            form.setFieldValue('ageMin', val ? parseInt(val) : undefined)
                                        }
                                        data={[
                                            { value: '', label: 'No minimum' },
                                            ...AGE_MIN_OPTIONS.map((age) => ({
                                                value: age.toString(),
                                                label: age.toString(),
                                            })),
                                        ]}
                                        disabled={isSubmitting}
                                    />
                                    <Select
                                        label="Maximum Age"
                                        value={form.values.ageMax?.toString() || ''}
                                        onChange={(val) =>
                                            form.setFieldValue('ageMax', val ? parseInt(val) : undefined)
                                        }
                                        data={[
                                            { value: '', label: 'No maximum' },
                                            ...AGE_MAX_OPTIONS.map((age) => ({
                                                value: age.toString(),
                                                label: age.toString(),
                                            })),
                                        ]}
                                        disabled={isSubmitting}
                                    />
                                </SimpleGrid>

                                <Box>
                                    <Text fw={500} mb={4}>
                                        Operating System
                                    </Text>
                                    <Stack gap="xs">
                                        {OS_OPTIONS.map((os) => (
                                            <Checkbox
                                                key={os.code}
                                                checked={
                                                    form.values.userOs?.some((item) => item.os === os.code) || false
                                                }
                                                onChange={() => toggleOS(os.code)}
                                                disabled={isSubmitting}
                                                label={os.label}
                                            />
                                        ))}
                                    </Stack>
                                    <Text size="xs" c="dimmed" mt={4}>
                                        Leave unchecked to target all operating systems
                                    </Text>
                                </Box>

                                <Box>
                                    <Text fw={500} mb={4}>
                                        Ad Placements
                                    </Text>
                                    <Paper
                                        withBorder
                                        radius="sm"
                                        p="xs"
                                        component={Stack}
                                        gap="xs"
                                        style={{
                                            maxHeight: 384,
                                            overflowY: 'auto',
                                        }}
                                    >
                                        {PLACEMENT_OPTIONS.map((placement) => (
                                            <Checkbox
                                                key={placement.code}
                                                checked={form.values.placements?.includes(placement.code) || false}
                                                onChange={() => togglePlacement(placement.code)}
                                                disabled={isSubmitting}
                                                label={placement.label}
                                            />
                                        ))}
                                    </Paper>
                                    <Text size="xs" c="dimmed" mt={4}>
                                        Leave unchecked for automatic placement across all available channels
                                    </Text>
                                </Box>
                            </>
                        )}
                    </Stack>
                </Box>

                {/* Bidding Section */}
                <Divider />
                <Box>
                    <Title order={4} mb="md">
                        Bidding
                    </Title>

                    <Stack gap="md">
                        <Select
                            label="Bidding Optimization"
                            required
                            data={biddingOptimizationData}
                            disabled={isSubmitting || form.values.targetingMode === 'AUTO'}
                            description={
                                form.values.targetingMode === 'AUTO'
                                    ? 'Auto targeting requires click optimization'
                                    : form.values.autoBidType === 'MANUAL'
                                      ? 'Manually set your bid amount'
                                      : 'Let LINE automatically optimize your bids'
                            }
                            value={form.values.autoBidType}
                            onChange={(val) => {
                                if (val !== null) form.setFieldValue('autoBidType', val);
                            }}
                        />

                        {form.values.autoBidType !== 'MANUAL' && (
                            <Select
                                label="Bid Strategy"
                                data={[
                                    { value: 'LOWEST_COST', label: 'Lowest Cost (No Limit)' },
                                    { value: 'COST_CAP', label: 'Cost Cap (Set Target Cost)' },
                                    { value: 'TARGET_COST', label: 'Target Cost' },
                                ]}
                                disabled={isSubmitting}
                                description={
                                    form.values.bidStrategy === 'LOWEST_COST'
                                        ? 'Spend budget as aggressively as possible'
                                        : 'Set a target cost per result'
                                }
                                value={form.values.bidStrategy || 'LOWEST_COST'}
                                onChange={(val) => {
                                    if (val !== null) form.setFieldValue('bidStrategy', val);
                                }}
                            />
                        )}
                    </Stack>
                </Box>

                <Divider />
                <Group justify="flex-end" gap="sm">
                    <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Creating...' : 'Create Ad Group'}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
